import { useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Skeleton } from '@/components/ui/skeleton'
import { getServiceInstanceIdFromSearch, ORG_ORGANIZATION_PAGE_PATH, withServiceInstanceId } from '@/lib/service-instance'
import { orgTreeApi } from '@/services/org-tree'

/**
 * `/org/service-instance?service_instance_id=…` — resolves the org tree node (primary_node)
 * and redirects to `/org/service-instance/:nodeId` with the same query string.
 */
export default function OrgServiceInstanceListRootRedirect() {
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const hasShownFallbackToastRef = useRef(false)

  const instanceQuery = useQuery({
    queryKey: ['org-service-instance-list-root', serviceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const primaryNode = instanceQuery.data?.primary_node
  const shouldFallbackToOrganization = Boolean(
    serviceInstanceId
    && !instanceQuery.isLoading
    && (instanceQuery.isError || primaryNode == null || String(primaryNode).trim() === ''),
  )

  useEffect(() => {
    if (!shouldFallbackToOrganization || hasShownFallbackToastRef.current) {
      return
    }

    hasShownFallbackToastRef.current = true
    toast.warning('Primary node not found for this service instance. Redirected to My Organisation.')
  }, [shouldFallbackToOrganization])

  if (!serviceInstanceId) {
    return <Navigate to={withServiceInstanceId(ORG_ORGANIZATION_PAGE_PATH, null)} replace />
  }

  if (instanceQuery.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-12 w-full max-w-md rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-[28px]" />
      </div>
    )
  }

  if (shouldFallbackToOrganization) {
    return <Navigate to={withServiceInstanceId(ORG_ORGANIZATION_PAGE_PATH, serviceInstanceId)} replace />
  }

  const nodeId = String(primaryNode).trim()
  return <Navigate to={`/org/service-instance/${nodeId}${location.search}${location.hash}`} replace />
}
